import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquare, Send, Trash2, Mail, Phone, ArrowLeft } from "lucide-react";
import { format, isToday, isYesterday } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface MessageRow {
  id: string;
  user_id: string | null;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  status: string;
  read_by_admin: boolean;
  created_at: string;
}
interface Reply {
  id: string;
  message_id: string;
  reply: string;
  created_at: string;
}
interface Conversation {
  key: string; // user_id or email
  name: string;
  email: string;
  phone: string | null;
  user_id: string | null;
  messages: MessageRow[];
  unread: number;
  lastAt: string;
  lastPreview: string;
}
type Bubble =
  | { kind: "user"; id: string; text: string; at: string }
  | { kind: "admin"; id: string; text: string; at: string };

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  if (isToday(d)) return format(d, "p");
  if (isYesterday(d)) return "Yesterday " + format(d, "p");
  return format(d, "MMM d, p");
};

const AdminMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [replies, setReplies] = useState<Record<string, Reply[]>>({});
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .order("created_at", { ascending: true });
    const list = (data ?? []) as MessageRow[];
    setMessages(list);
    if (list.length) {
      const ids = list.map((m) => m.id);
      const { data: reps } = await supabase
        .from("message_replies")
        .select("*")
        .in("message_id", ids)
        .order("created_at", { ascending: true });
      const grouped: Record<string, Reply[]> = {};
      ((reps ?? []) as Reply[]).forEach((r) => {
        (grouped[r.message_id] ||= []).push(r);
      });
      setReplies(grouped);
    } else {
      setReplies({});
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-messages-chat")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "message_replies" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const conversations: Conversation[] = useMemo(() => {
    const map = new Map<string, Conversation>();
    for (const m of messages) {
      const key = m.user_id || `email:${m.email.toLowerCase()}`;
      const c =
        map.get(key) ?? {
          key,
          name: m.name,
          email: m.email,
          phone: m.phone,
          user_id: m.user_id,
          messages: [],
          unread: 0,
          lastAt: m.created_at,
          lastPreview: m.message,
        };
      c.messages.push(m);
      if (!m.read_by_admin) c.unread += 1;
      if (+new Date(m.created_at) >= +new Date(c.lastAt)) {
        c.lastAt = m.created_at;
        c.lastPreview = m.message;
        c.name = m.name;
        c.phone = m.phone || c.phone;
      }
      map.set(key, c);
    }
    return Array.from(map.values()).sort(
      (a, b) => +new Date(b.lastAt) - +new Date(a.lastAt),
    );
  }, [messages]);

  const active = conversations.find((c) => c.key === activeKey) ?? null;

  const bubbles: Bubble[] = useMemo(() => {
    if (!active) return [];
    const u: Bubble[] = active.messages.map((m) => ({
      kind: "user",
      id: "m-" + m.id,
      text: m.message,
      at: m.created_at,
    }));
    const a: Bubble[] = [];
    active.messages.forEach((m) => {
      (replies[m.id] ?? []).forEach((r) => {
        a.push({ kind: "admin", id: "r-" + r.id, text: r.reply, at: r.created_at });
      });
    });
    return [...u, ...a].sort((x, y) => +new Date(x.at) - +new Date(y.at));
  }, [active, replies]);

  // Auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [bubbles.length, activeKey]);

  // Mark conversation messages as read when opened
  useEffect(() => {
    if (!active) return;
    const unreadIds = active.messages.filter((m) => !m.read_by_admin).map((m) => m.id);
    if (!unreadIds.length) return;
    supabase
      .from("messages")
      .update({ read_by_admin: true })
      .in("id", unreadIds)
      .then(() => load());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  const sendReply = async () => {
    if (!active || !text.trim()) return;
    // Reply on the most recent message in the conversation
    const lastMsg = [...active.messages].sort(
      (a, b) => +new Date(b.created_at) - +new Date(a.created_at),
    )[0];
    if (!lastMsg) return;
    setSending(true);
    const { error } = await supabase.from("message_replies").insert({
      message_id: lastMsg.id,
      reply: text.trim(),
      author_id: user?.id ?? null,
    });
    if (error) {
      toast.error(error.message);
      setSending(false);
      return;
    }
    await supabase
      .from("messages")
      .update({ status: "replied" })
      .in("id", active.messages.map((m) => m.id));
    setText("");
    setSending(false);
  };

  const deleteConversation = async () => {
    if (!active) return;
    if (!confirm(`Delete entire conversation with ${active.name}?`)) return;
    const { error } = await supabase
      .from("messages")
      .delete()
      .in("id", active.messages.map((m) => m.id));
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Conversation deleted");
    setActiveKey(null);
  };

  const totalUnread = conversations.reduce((s, c) => s + c.unread, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold">Messages</h1>
          <p className="text-muted-foreground">Chat with customers in real time.</p>
        </div>
        {totalUnread > 0 && (
          <Badge className="bg-destructive text-destructive-foreground">
            {totalUnread} unread
          </Badge>
        )}
      </div>

      <Card className="overflow-hidden">
        <div className="grid md:grid-cols-[320px_1fr] h-[calc(100vh-220px)] min-h-[500px]">
          {/* Conversation list */}
          <aside
            className={cn(
              "border-r bg-muted/20 flex flex-col min-h-0",
              active ? "hidden md:flex" : "flex",
            )}
          >
            <div className="px-4 py-3 border-b">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Conversations
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-3 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2" />
                  No messages yet.
                </div>
              ) : (
                <ul>
                  {conversations.map((c) => (
                    <li key={c.key}>
                      <button
                        onClick={() => setActiveKey(c.key)}
                        className={cn(
                          "w-full text-left px-4 py-3 border-b hover:bg-muted/60 transition-colors flex items-start gap-3",
                          activeKey === c.key && "bg-muted",
                        )}
                      >
                        <div className="h-10 w-10 rounded-full bg-gradient-primary text-primary-foreground grid place-items-center text-sm font-bold shrink-0">
                          {c.name.slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-sm truncate">{c.name}</p>
                            <span className="text-[10px] text-muted-foreground shrink-0">
                              {fmtTime(c.lastAt)}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {c.lastPreview}
                          </p>
                        </div>
                        {c.unread > 0 && (
                          <span className="min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold">
                            {c.unread}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* Chat panel */}
          <section className={cn("flex flex-col min-h-0", !active && "hidden md:flex")}>
            {!active ? (
              <div className="flex-1 grid place-items-center text-center p-8 text-muted-foreground">
                <div>
                  <MessageSquare className="h-10 w-10 mx-auto mb-2" />
                  <p className="text-sm">Select a conversation to start chatting.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden -ml-2"
                    onClick={() => setActiveKey(null)}
                    aria-label="Back"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="h-10 w-10 rounded-full bg-gradient-primary text-primary-foreground grid place-items-center text-sm font-bold">
                    {active.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{active.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {active.email}
                      </span>
                      {active.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {active.phone}
                        </span>
                      )}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={deleteConversation} aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Bubbles */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2 bg-muted/20">
                  {bubbles.map((b, i) => {
                    const prev = bubbles[i - 1];
                    const showTime =
                      !prev ||
                      prev.kind !== b.kind ||
                      +new Date(b.at) - +new Date(prev.at) > 5 * 60 * 1000;
                    const isAdmin = b.kind === "admin";
                    return (
                      <div key={b.id} className="animate-fade-in">
                        {showTime && (
                          <p
                            className={cn(
                              "text-[10px] text-muted-foreground mb-1 mt-2",
                              isAdmin ? "text-right pr-2" : "pl-2",
                            )}
                          >
                            {fmtTime(b.at)}
                          </p>
                        )}
                        <div className={cn("flex", isAdmin ? "justify-end" : "justify-start")}>
                          <div
                            className={cn(
                              "max-w-[80%] md:max-w-[70%] rounded-2xl px-3.5 py-2 text-sm shadow-sm whitespace-pre-wrap break-words",
                              isAdmin
                                ? "bg-primary text-primary-foreground rounded-br-md"
                                : "bg-background border rounded-bl-md",
                            )}
                          >
                            {b.text}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Composer */}
                <div className="border-t p-2.5 bg-background">
                  <div className="flex items-center gap-2">
                    <Input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      placeholder="Type a reply…"
                      maxLength={2000}
                      className="rounded-full"
                    />
                    <Button
                      onClick={sendReply}
                      disabled={sending || !text.trim()}
                      size="icon"
                      className="h-10 w-10 shrink-0 rounded-full"
                      aria-label="Send"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </Card>
    </div>
  );
};

export default AdminMessages;
